import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

import { useAppSelector } from '../store/hooks';
import { selectClaimedPrizes } from '../store/selectors';
import { NUMBER_OF_PRIZES } from '../constants/gameConfig';
import { useSettingsRedux } from '../hooks/useSettingsRedux';
import { useProductData } from '../hooks/product/useProductData';
import { convertRichTextToHTML } from '../components/product/utils';
import { useBuiltInTranslations } from '../hooks/useBuiltInTranslations';

// Import the new smaller components
import ProductDetails from '../components/product/ProductDetails';
import ProductVariations from '../components/product/ProductVariations';
import ProductMetafields from '../components/product/ProductMetafields';
import ProductGallery from '../components/product/ProductGallery';

import './Product.css';

const Product: React.FC = () => {
  const { productId = 'product1' } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // State for managing fade effects
  const [isVisible, setIsVisible] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const productPageRef = useRef<HTMLDivElement>(null);

  // Local state for UI
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isSizeFitExpanded, setIsSizeFitExpanded] = useState(false);

  // Use the custom hook for product data
  const {
    isLoading,
    currentProduct,
    productData,
    variantImages,
    selectedVariations,
    getMetafieldValue,
    getReviewsCount,
    getReviewsAverage,
    getPreOrderDate,
    handleVariationChange,
    handleColorVariantChange,
  } = useProductData(productId);

  // Redux selectors
  const claimedPrizes = useAppSelector(selectClaimedPrizes);

  // Calculate if all prizes are collected
  const allPrizesCollected = claimedPrizes >= NUMBER_OF_PRIZES;

  // Translation labels
  const { settings } = useSettingsRedux();
  const { t } = useBuiltInTranslations();
  const productTranslations = (settings?.translations?.product ?? {}) as Record<string, string>;
  const labelPrice = productTranslations.price ?? 'Price';
  const labelDiscount = productTranslations.discount ?? 'Discount';
  const labelTotal = productTranslations.total ?? 'Total';
  const labelShowMore = productTranslations.showMore ?? 'Show more';
  const labelShowLess = productTranslations.showLess ?? 'Show less';
  const labelBack = productTranslations.back ?? 'Back';
  const labelReviews = productTranslations.reviews ?? 'reviews';
  const labelColor = productTranslations.color ?? 'Colors';
  const labelSizeChart = productTranslations.sizeChart ?? 'Size Guide';
  const labelPreOrder = productTranslations.preOrder ?? 'Pre-order - Available {date}';

  // Track when component is first mounted
  useEffect(() => {
    setIsInitialized(true);
    // Trigger fade-in when component mounts
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50); // Small delay to ensure the component is rendered

    return () => clearTimeout(timer);
  }, []);

  // Handle fade effects when location changes
  useEffect(() => {
    if (!isInitialized) return;

    const rootPath = process.env.REACT_APP_ROOT_PATH || '';
    const isProductRoute = location.pathname.includes('/product/');

    if (isProductRoute) {
      // Fade in when returning to Product page
      setIsVisible(false); // Start from invisible
      const timer = setTimeout(() => {
        setIsVisible(true); // Then fade in
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [location.pathname, isInitialized]);

  // Apply opacity to product page based on visibility state
  useEffect(() => {
    if (productPageRef.current) {
      productPageRef.current.style.opacity = isVisible ? '1' : '0';
    }
  }, [isVisible]);

  // Reset visibility when component unmounts to avoid stale state
  useEffect(() => {
    return () => {
      setIsVisible(false);
    };
  }, []);

  const handleGoBack = () => {
    if (isTransitioning) return; // Prevent multiple rapid clicks

    const rootPath = process.env.REACT_APP_ROOT_PATH || '';
    // Trigger fade out before navigation
    setIsVisible(false);
    setIsTransitioning(true);
    // Small delay to allow fade effect to show before navigation
    setTimeout(() => {
      // Dispatch a custom popstate event to trigger the back animation
      window.dispatchEvent(new PopStateEvent('popstate'));
      // Navigate back to the game path
      navigate(`${rootPath}/game`);
      setIsTransitioning(false);
    }, 150);
  };

  const toggleDescriptionExpand = () => {
    setIsDescriptionExpanded(!isDescriptionExpanded);
  };

  const toggleSizeFitExpand = () => {
    setIsSizeFitExpanded(!isSizeFitExpanded);
  };

  return (
    <div className="product-page" ref={productPageRef}>
      <button className="back-button" onClick={handleGoBack}>
        <span className="back-button-icon">←</span> {labelBack}
      </button>
      <div className="product-content">
        {isLoading ? (
          <div className="loading">{t('loading')}</div>
        ) : productData ? (
          <>
            <ProductDetails
              productData={productData}
              currentProduct={currentProduct}
              getReviewsCount={getReviewsCount}
              getReviewsAverage={getReviewsAverage}
              allPrizesCollected={allPrizesCollected}
              labelPrice={labelPrice}
              labelDiscount={labelDiscount}
              labelTotal={labelTotal}
              labelReviews={labelReviews}
              isDescriptionExpanded={isDescriptionExpanded}
              labelShowMore={labelShowMore}
              labelShowLess={labelShowLess}
              onToggleDescription={toggleDescriptionExpand}
            />

            <div className="variation-container">
              <ProductVariations
                productData={productData}
                currentProduct={currentProduct}
                selectedVariations={selectedVariations}
                variantImages={variantImages}
                labelColor={labelColor}
                labelSizeChart={labelSizeChart}
                getPreOrderDate={getPreOrderDate}
                labelPreOrder={labelPreOrder}
                onVariationChange={handleVariationChange}
                onColorVariantChange={handleColorVariantChange}
              />
            </div>

            <ProductMetafields
              currentProduct={currentProduct}
              getMetafieldValue={getMetafieldValue}
              convertRichTextToHTML={convertRichTextToHTML}
              isSizeFitExpanded={isSizeFitExpanded}
              labelShowMore={labelShowMore}
              labelShowLess={labelShowLess}
              onToggleSizeFit={toggleSizeFitExpand}
            />

            <ProductGallery productData={productData} currentProduct={currentProduct} />
          </>
        ) : (
          <div className="error">Product not found</div>
        )}
      </div>
    </div>
  );
};

export default Product;
